import {isLoggedIn} from '@github-ui/client-env'
import {SpammyBadge} from '@github-ui/commenting/SpammyBadge'
import {usePinnedComment} from '@github-ui/commenting/usePinnedComment'
import {copyText} from '@github-ui/copy-to-clipboard'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {ProfileReference} from '@github-ui/profile-reference'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ArrowDownIcon, KebabHorizontalIcon, LinkIcon, PinIcon, PinSlashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link, RelativeTime} from '@primer/react'
import {Suspense, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {PinnedCommentDisplay$data, PinnedCommentDisplay$key} from './__generated__/PinnedCommentDisplay.graphql'
import type {PinnedCommentDisplaySecondary$key} from './__generated__/PinnedCommentDisplaySecondary.graphql'
import styles from './PinnedCommentDisplay.module.css'

/**
 * Extracts the hash fragment from a comment URL so that navigation stays
 * in-page instead of forcing a full redirect to the issues show route.
 */
function getCommentHash(url?: string): string {
  if (!url) return ''
  const hashIndex = url.indexOf('#')
  return hashIndex !== -1 ? url.slice(hashIndex) : url
}

export type PinnedCommentDisplayProps = {
  issue: PinnedCommentDisplay$key
  secondaryKey?: PinnedCommentDisplaySecondary$key | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PinnedCommentDisplaySecondaryFragment = graphql`
  fragment PinnedCommentDisplaySecondary on Issue {
    pinnedIssueComment {
      issueComment {
        showSpammyBadge
      }
    }
  }
`

export function PinnedCommentDisplay({issue, secondaryKey}: PinnedCommentDisplayProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment PinnedCommentDisplay on Issue {
        pinnedIssueComment {
          pinnedBy {
            login
          }
          issueComment {
            id
            shortBodyHTML(limit: 700, stripBlockElements: false)
            createdAt
            url
            viewerCanUnpin
            author {
              login
              avatarUrl
              profileUrl
            }
          }
        }
      }
    `,
    issue,
  )

  const secondaryData = useFragment(PinnedCommentDisplaySecondaryFragment, secondaryKey ?? null)

  if (!data.pinnedIssueComment?.issueComment) {
    return null
  }

  const {issueComment: comment, pinnedBy} = data.pinnedIssueComment
  const showSpammyBadge = secondaryData?.pinnedIssueComment?.issueComment?.showSpammyBadge ?? false
  const commentHash = getCommentHash(comment.url)

  return (
    <Suspense fallback={null}>
      <div className={styles.PinnedCommentContainer} data-testid="pinned-comment-display">
        <PinnedCommentHeader
          pinnedBy={pinnedBy}
          commentId={comment.id}
          viewerCanUnpin={comment.viewerCanUnpin}
          url={comment.url}
          showSpammyBadge={showSpammyBadge}
        />
        {comment.author && <AuthorInfo comment={comment} />}
        <NewMarkdownViewer
          className={`${styles.CommentBody} Links--underline`}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to GraphQL fragment PinnedCommentDisplay.shortBodyHTML */
          verifiedHTML={comment.shortBodyHTML as SafeHTMLString}
        />
        <div className={styles.FullCommentLink}>
          <Link href={commentHash}>
            View full comment <ArrowDownIcon size={16} />
          </Link>
        </div>
      </div>
    </Suspense>
  )
}

type PinnedCommentHeaderProps = {
  pinnedBy: {
    login: string
  }
  commentId: string
  viewerCanUnpin: boolean
  url: string
  showSpammyBadge: boolean
}

type MenuAction = {
  key: string
  label: string
  icon: React.ComponentType
  onSelect: () => void
  ariaLabel: string
  visible: boolean
}

function PinnedCommentHeader({pinnedBy, commentId, viewerCanUnpin, url, showSpammyBadge}: PinnedCommentHeaderProps) {
  const {unpinComment} = usePinnedComment()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const handleUnpin = () => {
    unpinComment(commentId)
  }

  const handleCopyLink = () => {
    copyText(url)
  }

  const menuActions: MenuAction[] = [
    {
      key: 'copy-link',
      label: 'Copy link',
      icon: LinkIcon,
      onSelect: handleCopyLink,
      ariaLabel: 'Copy link to pinned comment',
      visible: true,
    },
    {
      key: 'unpin',
      label: 'Unpin',
      icon: PinSlashIcon,
      onSelect: handleUnpin,
      ariaLabel: 'Unpin comment',
      visible: isLoggedIn() && viewerCanUnpin,
    },
  ]

  const visibleActions = menuActions.filter(action => action.visible)

  return (
    <div className={styles.PinnedCommentHeader}>
      <div className={styles.PinnedByInfo}>
        <PinIcon size={14} className={styles.PinnedByIcon} />
        {pinnedBy && (
          <>
            Pinned by{' '}
            <Link href={`/${pinnedBy.login}`} {...getHovercardAttributesForType('user', {login: pinnedBy.login})}>
              <ProfileReference login={pinnedBy.login} isAgent={false} />
            </Link>
          </>
        )}
      </div>
      <div className={styles.HeaderActions}>
        {showSpammyBadge && <SpammyBadge />}
        {visibleActions.length > 0 && (
          <ActionMenu anchorRef={menuButtonRef}>
            <ActionMenu.Anchor>
              <IconButton
                ref={menuButtonRef}
                size="small"
                icon={KebabHorizontalIcon}
                variant="invisible"
                aria-label="Pinned comment options"
                className={styles.ActionsMenuButton}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="auto">
              <ActionList>
                {visibleActions.map(action => (
                  <ActionList.Item key={action.key} onSelect={action.onSelect} aria-label={action.ariaLabel}>
                    <ActionList.LeadingVisual>
                      <action.icon />
                    </ActionList.LeadingVisual>
                    {action.label}
                  </ActionList.Item>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </div>
    </div>
  )
}

type AuthorInfoProps = {
  comment: NonNullable<PinnedCommentDisplay$data['pinnedIssueComment']>['issueComment']
}

function AuthorInfo({comment}: AuthorInfoProps) {
  const createdAt = new Date(comment.createdAt)
  const author = comment.author!
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  return (
    <div className={styles.AuthorInfo}>
      <Link
        href={author.profileUrl ?? undefined}
        {...getHovercardAttributesForType('user', {login: author.login})}
        className={styles.AuthorLink}
      >
        <GitHubAvatar className={styles.Avatar} src={author.avatarUrl} size={25} alt={`@${author.login}`} />
        {author.login}
      </Link>
      <Link href={getCommentHash(comment.url)} data-testid="issue-body-header-link" className={styles.TimestampLink}>
        <RelativeTime date={createdAt}>
          on {createdAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
        </RelativeTime>
      </Link>
    </div>
  )
}
